// ============================================================================
// @torre/expenses — Ritmo de gasto y ajuste semanal de presupuestos (#1178)
// ============================================================================
// UNA sola regla para todo lo que proyecta o ajusta presupuestos, para que
// Presupuestos, las gráficas de Gastos y el ajuste automático den lo mismo:
//
//   · RITMO de una categoría: promedio de los últimos 3 meses CERRADOS en que
//     tuvo gasto (buscando hasta 6 meses atrás). Un mes sin estado de cuenta
//     cargado no la jala a cero, y el mes en curso (a medias) no la sesga.
//     Nómina es la excepción: su ritmo es el último mes cerrado con gasto, porque
//     el sueldo es fijo y el promedio arrastraría meses con otro equipo.
//   · AJUSTE SEMANAL: cada línea MENSUAL de presupuesto del mes en curso en
//     adelante (del mismo año) toma el ritmo de su categoría, redondeado hacia
//     arriba a $100. No toca líneas anuales, ni líneas marcadas «no ajustar»
//     (`autoAdjust: false`), ni categorías sin gasto reciente. No crea meses
//     nuevos: el plan sigue cubriendo los meses que eligió la persona.
//
// Lógica pura: el front la usa para la vista previa y las gráficas; el back
// (vía `build:functions`) para el trabajo programado de cada lunes.
// ============================================================================

import { EXPENSE_CATEGORIES, isFixedCategory, round2, type BudgetLine, type ExpenseCategory } from "./index";
import type { CategorySpendMonth } from "./categorySpend";

/** Meses con gasto que se promedian para el ritmo. */
export const RUN_RATE_MONTHS = 3;
/** Hasta cuántos meses cerrados atrás se buscan esos meses con gasto. */
export const RUN_RATE_LOOKBACK = 6;
/** Categorías cuyo ritmo es el último mes cerrado con gasto (no el promedio). */
export const LATEST_MONTH_CATEGORIES: readonly ExpenseCategory[] = ["nomina"];
/** El ajuste redondea hacia arriba a este múltiplo. */
export const BUDGET_ROUNDING = 100;

const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const monthIndex = (period: string) => Number(period.slice(0, 4)) * 12 + Number(period.slice(5, 7)) - 1;
const periodAt = (index: number) => `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;

export interface CategoryRunRate {
  category: ExpenseCategory;
  /** Gasto mensual esperado (moneda base). */
  rate: number;
  /** Parte fija del ritmo (lo demás es variable). */
  fixed: number;
  /** Meses que se usaron, del más reciente al más viejo. */
  basis: string[];
  method: "promedio" | "ultimo-mes";
}

/**
 * Ritmo de gasto por categoría a partir del gasto mensual (`categorySpend().months`).
 * Solo cuentan meses CERRADOS (anteriores al mes de `today`) con gasto > 0.
 */
export function categoryRunRates(months: readonly CategorySpendMonth[], today: string): Partial<Record<ExpenseCategory, CategoryRunRate>> {
  const current = String(today || "").slice(0, 7);
  if (!PERIOD_RE.test(current)) return {};
  const byPeriod = new Map(months.filter((m) => PERIOD_RE.test(m.period)).map((m) => [m.period, m]));
  const base = monthIndex(current);
  const lookback = Array.from({ length: RUN_RATE_LOOKBACK }, (_, i) => periodAt(base - 1 - i));
  const out: Partial<Record<ExpenseCategory, CategoryRunRate>> = {};
  // Las del sistema y, además, toda categoría propia de la compañía que tenga gasto (#1255).
  const categories = new Set<string>(EXPENSE_CATEGORIES);
  for (const p of lookback) for (const c of Object.keys(byPeriod.get(p)?.byCategory || {})) categories.add(c);
  for (const category of categories) {
    const wanted = LATEST_MONTH_CATEGORIES.includes(category) ? 1 : RUN_RATE_MONTHS;
    const basis: string[] = [];
    let total = 0;
    let fixed = 0;
    for (const p of lookback) {
      if (basis.length >= wanted) break;
      const m = byPeriod.get(p);
      const amount = m?.byCategory[category] || 0;
      if (amount <= 0) continue;
      basis.push(p);
      total += amount;
      // Sin el desglose por etiqueta, la categoría decide el tipo.
      const fx = m?.fixedByCategory ? m.fixedByCategory[category] || 0 : isFixedCategory(category) ? amount : 0;
      fixed += Math.min(amount, Math.max(0, fx));
    }
    if (!basis.length) continue;
    out[category] = {
      category,
      rate: round2(total / basis.length),
      fixed: round2(fixed / basis.length),
      basis,
      method: wanted === 1 ? "ultimo-mes" : "promedio",
    };
  }
  return out;
}

/** Una línea de presupuesto con lo que el ajuste necesita saber. */
export type AdjustableBudgetLine = Pick<BudgetLine, "id" | "period" | "category" | "amount"> & { autoAdjust?: boolean | null };

export interface BudgetAdjustment {
  lineId: string;
  period: string;
  category: ExpenseCategory;
  before: number;
  after: number;
  rate: number;
  basis: string[];
  method: CategoryRunRate["method"];
}

export interface BudgetAdjustmentSkip {
  lineId: string;
  period: string;
  category: ExpenseCategory;
  reason: "fijada" | "sin-gasto-reciente";
}

export interface BudgetAdjustmentPlan {
  /** Mes en curso (`YYYY-MM`): se ajusta de aquí a diciembre. */
  from: string;
  changes: BudgetAdjustment[];
  /** Líneas que se dejaron igual y por qué (las que ya coinciden no aparecen). */
  skipped: BudgetAdjustmentSkip[];
  /** Total del presupuesto ajustable antes y después. */
  totalBefore: number;
  totalAfter: number;
}

const roundUp = (value: number, step = BUDGET_ROUNDING) => (value <= 0 ? 0 : Math.ceil(value / step) * step);

/**
 * Propuesta del ajuste semanal: qué líneas mensuales cambian, de cuánto a
 * cuánto y con qué meses de base. No escribe nada.
 */
export function proposeBudgetAdjustments(input: {
  budgets: readonly AdjustableBudgetLine[];
  months: readonly CategorySpendMonth[];
  today: string;
}): BudgetAdjustmentPlan {
  const from = String(input.today || "").slice(0, 7);
  const empty: BudgetAdjustmentPlan = { from, changes: [], skipped: [], totalBefore: 0, totalAfter: 0 };
  if (!PERIOD_RE.test(from)) return empty;
  const rates = categoryRunRates(input.months, input.today);
  const plan = { ...empty };
  const candidates = input.budgets
    .filter((b) => PERIOD_RE.test(b.period) && b.period >= from && b.period.slice(0, 4) === from.slice(0, 4))
    .sort((a, b) => a.period.localeCompare(b.period) || a.category.localeCompare(b.category));
  for (const line of candidates) {
    const before = round2(Number(line.amount) || 0);
    if (line.autoAdjust === false) {
      plan.skipped.push({ lineId: line.id, period: line.period, category: line.category, reason: "fijada" });
      plan.totalBefore += before;
      plan.totalAfter += before;
      continue;
    }
    const r = rates[line.category];
    if (!r || r.rate <= 0) {
      plan.skipped.push({ lineId: line.id, period: line.period, category: line.category, reason: "sin-gasto-reciente" });
      plan.totalBefore += before;
      plan.totalAfter += before;
      continue;
    }
    const after = roundUp(r.rate);
    plan.totalBefore += before;
    plan.totalAfter += after;
    if (Math.abs(after - before) < 0.5) continue;
    plan.changes.push({ lineId: line.id, period: line.period, category: line.category, before, after, rate: r.rate, basis: r.basis, method: r.method });
  }
  plan.totalBefore = round2(plan.totalBefore);
  plan.totalAfter = round2(plan.totalAfter);
  return plan;
}

/** Nota que deja el ajuste en la línea, legible en la tabla de líneas. */
export function adjustmentNote(change: Pick<BudgetAdjustment, "rate" | "basis" | "method">, runDate: string): string {
  const months = change.basis.join(", ");
  const how = change.method === "ultimo-mes" ? `último mes cerrado (${months})` : `promedio de ${change.basis.length} meses cerrados con gasto (${months})`;
  return `Ajuste automático ${runDate}: ritmo $${round2(change.rate).toLocaleString("es-MX")} = ${how}.`;
}
